import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor for handling token refresh
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If it's the refresh endpoint failing, redirect to login
      if (originalRequest.url === '/auth/refresh') {
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        await api.post('/auth/refresh');
        processQueue(null);
        isRefreshing = false;
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        isRefreshing = false;
        // Redirect to login if refresh fails
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboard = () => api.get('/dashboard');

// Auth
export const register = (data) => api.post('/auth/register', data);
export const approveUser = (userId) => api.post('/auth/approve-user', { user_id: userId });
export const rejectUser = (userId) => api.post('/auth/reject-user', { user_id: userId });
export const deactivateUser = (userId) => api.post(`/users/${userId}/deactivate`);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// Income
export const getIncome = (startDate, endDate) => 
  api.get('/income', { params: { start_date: startDate, end_date: endDate } });
export const createIncome = (data) => api.post('/income', data);
export const updateIncome = (id, data) => api.put(`/income/${id}`, data);
export const deleteIncome = (id) => api.delete(`/income/${id}`);
export const bulkDeleteIncome = (ids) => api.post('/income/bulk-delete', { ids });

// Expense
export const getExpense = (startDate, endDate) => 
  api.get('/expense', { params: { start_date: startDate, end_date: endDate } });
export const createExpense = (data) => api.post('/expense', data);
export const updateExpense = (id, data) => api.put(`/expense/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expense/${id}`);
export const bulkDeleteExpense = (ids) => api.post('/expense/bulk-delete', { ids });

// Bank Transfers
export const getBankTransfers = () => api.get('/transfer/bank');
export const createBankTransfer = (data) => api.post('/transfer/bank', data);

// User Transfers
export const createUserTransfer = (data) => api.post('/transfer/user', data);
export const updateUserTransfer = (id, data) => api.put(`/transfer/user/${id}`, data);
export const deleteUserTransfer = (id) => api.delete(`/transfer/user/${id}`);

// Customers (legacy)
export const getCustomers = () => api.get('/customers');
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const bulkDeleteCustomers = (ids) => api.post('/customers/bulk-delete', { ids });

// Customer Firms (Sales module)
export const getCustomerFirms = () => api.get('/customer-firms');
export const createCustomerFirm = (data) => api.post('/customer-firms', data);
export const updateCustomerFirm = (id, data) => api.put(`/customer-firms/${id}`, data);
export const deleteCustomerFirm = (id) => api.delete(`/customer-firms/${id}`);
export const bulkDeleteCustomerFirms = (ids) => api.post('/customer-firms/bulk-delete', { ids });

// Sales Orders (SO)
export const getSalesOrders = (customerFirmId, startDate, endDate) => api.get('/sales-orders', { params: { customer_firm_id: customerFirmId || undefined, start_date: startDate, end_date: endDate } });
export const getLastSalesOrder = (customerFirmId, productId) => api.get(`/sales-orders/last/${customerFirmId}`, { params: { product_id: productId || undefined } });
export const createSalesOrder = (data) => api.post('/sales-orders', data);
export const updateSalesOrder = (id, data) => api.put(`/sales-orders/${id}`, data);
export const deleteSalesOrder = (id) => api.delete(`/sales-orders/${id}`);
export const bulkDeleteSalesOrders = (ids) => api.post('/sales-orders/bulk-delete', { ids });

// Sales Entries
export const getSalesEntries = (soId, customerFirmId, startDate, endDate) => api.get('/sales-entries', { params: { sales_order_id: soId || undefined, customer_firm_id: customerFirmId || undefined, start_date: startDate, end_date: endDate } });
export const createSalesEntry = (data) => api.post('/sales-entries', data);
export const updateSalesEntry = (id, data) => api.put(`/sales-entries/${id}`, data);
export const deleteSalesEntry = (id) => api.delete(`/sales-entries/${id}`);
export const bulkDeleteSalesEntries = (ids) => api.post('/sales-entries/bulk-delete', { ids });

// SO Outstanding
export const getSOOutstanding = () => api.get('/sales/so-outstanding');
export const getSalesPaymentOutstanding = () => api.get('/sales/payment-outstanding');

// Sale Receipts
export const getSaleReceipts = (customerFirmId, status, startDate, endDate) => api.get('/sale-receipts', { params: { customer_firm_id: customerFirmId || undefined, status: status || undefined, start_date: startDate || undefined, end_date: endDate || undefined } });
export const createSaleReceipt = (data) => api.post('/sale-receipts', data);
export const clearSaleReceipt = (id, data) => api.post(`/sale-receipts/${id}/clear`, data);
export const bounceSaleReceipt = (id) => api.post(`/sale-receipts/${id}/bounce`);
export const deleteSaleReceipt = (id) => api.delete(`/sale-receipts/${id}`);

// Customer Ledger (new)
export const getCustomerLedgerSummary = () => api.get('/customer-ledger');
export const getCustomerLedgerDetail = (firmId, startDate, endDate) => api.get(`/customer-ledger/${firmId}`, { params: { start_date: startDate || undefined, end_date: endDate || undefined } });

// Sales Credit / Debit Notes
export const getSaleNotes = (params = {}) => api.get('/sale-notes', { params });
export const createSaleNote = (data) => api.post('/sale-notes', data);
export const updateSaleNote = (id, data) => api.put(`/sale-notes/${id}`, data);
export const deleteSaleNote = (id) => api.delete(`/sale-notes/${id}`);
export const bulkDeleteSaleNotes = (ids) => api.post('/sale-notes/bulk-delete', { ids });

// Purchase Credit / Debit Notes
export const getPurchaseNotes = (params = {}) => api.get('/purchase-notes', { params });
export const createPurchaseNote = (data) => api.post('/purchase-notes', data);
export const updatePurchaseNote = (id, data) => api.put(`/purchase-notes/${id}`, data);
export const deletePurchaseNote = (id) => api.delete(`/purchase-notes/${id}`);
export const bulkDeletePurchaseNotes = (ids) => api.post('/purchase-notes/bulk-delete', { ids });

// Sales (legacy - keep for backward compat)
export const getSales = (status, startDate, endDate) => api.get('/sales', { params: { status, start_date: startDate, end_date: endDate } });
export const createSale = (data) => api.post('/sales', data);
export const recordSalePayment = (data) => api.post('/sales/payment', data);
export const recordCustomerPayment = (data) => api.post('/sales/customer-payment', data);
export const getCustomerOutstandingSales = (customerId) => api.get(`/sales/customer-outstanding/${customerId}`);
export const getLastSaleRemarks = (customerId) => api.get(`/sales/last-remarks/${customerId}`);
export const bulkDeleteSales = (ids) => api.post('/sales/bulk-delete', { ids });
export const deleteSale = (id) => api.delete(`/sales/${id}`);
export const getOutstandingReport = () => api.get('/reports/outstanding');

// Recurring Entries
export const getRecurringEntries = () => api.get('/recurring');
export const createRecurringEntry = (data) => api.post('/recurring', data);
export const updateRecurringEntry = (id, data) => api.put(`/recurring/${id}`, data);
export const deleteRecurringEntry = (id) => api.delete(`/recurring/${id}`);
export const bulkDeleteRecurring = (ids) => api.post('/recurring/bulk-delete', { ids });
export const approveRecurringEntry = (recurringId, approve) => 
  api.post('/recurring/approve', { recurring_id: recurringId, approve });
export const postRecurringInstance = (recurringId, date, remarks) =>
  api.post('/recurring/post-instance', { recurring_id: recurringId, date, remarks: remarks || '' });
export const stopRecurringEntry = (recurringId, reason) =>
  api.post('/recurring/stop', { recurring_id: recurringId, reason });
export const restartRecurringEntry = (recurringId) =>
  api.post('/recurring/restart', { recurring_id: recurringId });

// Notifications
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/read-all');

// Ledger bulk delete
export const bulkDeleteLedgerEntries = (ids) => api.post('/ledger/bulk-delete', { ids });
export const bulkDeleteEntryLedgers = (ids) => api.post('/entry-ledgers/bulk-delete', { ids });

// Audit Trail
export const getAuditTrail = (entityType) => 
  api.get('/audit-trail', { params: { entity_type: entityType } });
export const clearAuditTrail = () => api.delete('/audit-trail');

// Users
export const getUsers = () => api.get('/users');
export const getAllUsersBasic = () => api.get('/users/all');
export const resetUserPassword = (userId) => api.post('/users/reset-password', { user_id: userId });

// Admin: Deleted Records & Recovery
export const getDeletedRecords = (recordType) => api.get('/admin/deleted-records', { params: { record_type: recordType } });
export const restoreDeletedRecords = (ids) => api.post('/admin/restore', { ids });
export const permanentDeleteRecords = (ids) => api.post('/admin/permanent-delete', { ids });

// Entry Ledgers
export const getEntryLedgers = () => api.get('/entry-ledgers');
export const createEntryLedger = (data) => api.post('/entry-ledgers', data);
export const updateEntryLedger = (id, data) => api.put(`/entry-ledgers/${id}`, data);
export const deleteEntryLedger = (id) => api.delete(`/entry-ledgers/${id}`);
export const getEntryLedgerDetail = (id, startDate, endDate) => api.get(`/entry-ledgers/${id}/ledger`, { params: { start_date: startDate, end_date: endDate } });
export const getEntryLedgerCategoryReport = (category) => api.get('/entry-ledgers/report/by-category', { params: { category: category || undefined } });

// Supplier Groups
export const getSupplierGroups = () => api.get('/supplier-groups');
export const getSupplierGroupDetail = (id) => api.get(`/supplier-groups/${id}`);
export const createSupplierGroup = (data) => api.post('/supplier-groups', data);
export const updateSupplierGroup = (id, data) => api.put(`/supplier-groups/${id}`, data);
export const deleteSupplierGroup = (id) => api.delete(`/supplier-groups/${id}`);

// Suppliers (Firms)
export const getSuppliers = (groupId) => api.get('/suppliers', { params: { group_id: groupId || undefined } });
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);

// Products
export const getProducts = (category, group) => api.get('/products', { params: { category: category || undefined, group: group || undefined } });
export const getProductsMeta = () => api.get('/products/meta');
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const bulkDeleteProducts = (ids) => api.post('/products/bulk-delete', { ids });

// Purchase Orders
export const getPurchaseOrders = (supplierGroupId, startDate, endDate) => api.get('/purchase-orders', { params: { supplier_group_id: supplierGroupId || undefined, start_date: startDate, end_date: endDate } });
export const getLastPurchaseOrder = (supplierGroupId, productId) => api.get(`/purchase-orders/last/${supplierGroupId}`, { params: { product_id: productId || undefined } });
export const createPurchaseOrder = (data) => api.post('/purchase-orders', data);
export const updatePurchaseOrder = (id, data) => api.put(`/purchase-orders/${id}`, data);
export const deletePurchaseOrder = (id) => api.delete(`/purchase-orders/${id}`);

// Purchase Entries
export const getPurchaseEntries = (poId, supplierId, startDate, endDate) => api.get('/purchase-entries', { params: { purchase_order_id: poId || undefined, supplier_id: supplierId || undefined, start_date: startDate, end_date: endDate } });
export const createPurchaseEntry = (data) => api.post('/purchase-entries', data);
export const updatePurchaseEntry = (id, data) => api.put(`/purchase-entries/${id}`, data);
export const deletePurchaseEntry = (id) => api.delete(`/purchase-entries/${id}`);
export const bulkDeletePurchaseOrders = (ids) => api.post('/purchase-orders/bulk-delete', { ids });
export const bulkDeletePurchaseEntries = (ids) => api.post('/purchase-entries/bulk-delete', { ids });
export const bulkDeleteSuppliers = (ids) => api.post('/suppliers/bulk-delete', { ids });

// Purchase Outstanding
export const getPurchaseOutstanding = () => api.get('/purchase/outstanding');
export const getPurchasePaymentOutstanding = () => api.get('/purchase/payment-outstanding');

// Purchase Payments (Unified)
export const getPurchasePayments = (supplierId, status, startDate, endDate) => api.get('/purchase-payments', { params: { supplier_id: supplierId || undefined, status: status || undefined, start_date: startDate || undefined, end_date: endDate || undefined } });
export const createPurchasePayment = (data) => api.post('/purchase-payments', data);
export const clearPurchasePayment = (id, data) => api.post(`/purchase-payments/${id}/clear`, data);
export const bouncePurchasePayment = (id) => api.post(`/purchase-payments/${id}/bounce`);
export const deletePurchasePayment = (id) => api.delete(`/purchase-payments/${id}`);

// Supplier Ledger
export const getSupplierLedgerSummary = () => api.get('/supplier-ledger');
export const getSupplierLedger = (supplierId, startDate, endDate) => api.get(`/supplier-ledger/${supplierId}`, { params: { start_date: startDate || undefined, end_date: endDate || undefined } });

// Batches
export const getBatches = (productId, category) => api.get('/batches', { params: { product_id: productId || undefined, category: category || undefined } });
export const getNextBatchNumber = (productId) => api.get(`/batches/next-number/${productId}`);
export const createBatch = (data) => api.post('/batches', data);
export const deleteBatch = (id) => api.delete(`/batches/${id}`);

// Inventory Stock
export const getInventoryStock = (category) => api.get('/inventory/stock', { params: { category: category || undefined } });
export const getInventoryOverview = (category) => api.get('/inventory/overview', { params: { category: category || undefined } });
export const getProductStockDetail = (productId) => api.get(`/inventory/stock/${productId}`);
export const updateProductThreshold = (productId, threshold) => api.put(`/products/${productId}/threshold`, { low_stock_threshold: threshold });

// Production
export const getProductions = (startDate, endDate) => api.get('/productions', { params: { start_date: startDate, end_date: endDate } });
export const createProduction = (data) => api.post('/productions', data);
export const deleteProduction = (id) => api.delete(`/productions/${id}`);

// Lab Reports
export const getLabReports = (productionId, batchId) => api.get('/lab-reports', { params: { production_id: productionId || undefined, batch_id: batchId || undefined } });
export const createLabReport = (data) => api.post('/lab-reports', data);
export const updateLabReport = (id, data) => api.put(`/lab-reports/${id}`, data);
export const deleteLabReport = (id) => api.delete(`/lab-reports/${id}`);

// Batch Timeline
export const getBatchTimeline = (batchId) => api.get(`/batch-timeline/${batchId}`);
export const getBatchTimelineProducts = (productId) => api.get('/batch-timeline-products', { params: { product_id: productId || undefined } });


// All Entries (Unified View)
export const getAllEntries = (startDate, endDate, entryType) => api.get('/all-entries', { params: { start_date: startDate, end_date: endDate, entry_type: entryType || undefined } });

// Reports
export const getReceivablesReport = () => api.get('/reports/receivables');
export const getExpenseReport = (startDate, endDate) => 
  api.get('/reports/expense', { params: { start_date: startDate, end_date: endDate } });
export const getIncomeReport = (startDate, endDate) => 
  api.get('/reports/income', { params: { start_date: startDate, end_date: endDate } });
export const getLedgerReport = () => api.get('/reports/ledger');
export const getCustomerLedger = (customerId, startDate, endDate) => api.get(`/customers/${customerId}/ledger`, { params: { start_date: startDate, end_date: endDate } });
export const getUserLedger = (userId, startDate, endDate) => api.get(`/users/${userId}/ledger`, { params: { start_date: startDate, end_date: endDate } });

export default api;

// HSN Master
export const getHSNCodes = () => api.get('/hsn');
export const createHSN = (data) => api.post('/hsn', data);
export const updateHSN = (id, data) => api.put(`/hsn/${id}`, data);
export const deleteHSN = (id) => api.delete(`/hsn/${id}`);
export const deleteHSNAll = (hsnCode) => api.delete(`/hsn/code/${hsnCode}`);

// Company Settings
export const getCompanySettings = () => api.get('/company-settings');
export const updateCompanySettings = (data) => api.put('/company-settings', data);

// HSN rate lookup by date
export const getHSNRateForDate = (hsnCode, date) => api.get(`/hsn/rate/${hsnCode}/${date}`);
